import Papa from 'papaparse';

// ── Types ─────────────────────────────────────────────────────────────────────
export type CsvFormat = 'selected-invoices' | 'smartbiz' | 'unknown';

export interface ParsedCsvLine {
  supplierCode: string;
  description: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  lineGst?: number;
  lineIncGst?: number;
}

export interface ParsedCsvInvoice {
  supplierInvoiceNo: string;
  docType: 'INV' | 'CRD';
  invoiceDate: string;              // ISO yyyy-mm-dd
  customerPoRef: string;            // customer's PO ref as recorded by supplier
  requesterLabel?: string;          // e.g. "ETHAN P", "MAIKO"
  supplierAbn?: string;
  subtotalExGst: number;
  totalGst: number;
  totalIncGst: number;
  lines: ParsedCsvLine[];
}

export interface CsvDetectionResult {
  format: CsvFormat;
  invoices: ParsedCsvInvoice[];
  supplierAbn?: string;
  totalInvoices: number;
  totalLines: number;
  totalValue: number;
  dateRange: { from: string; to: string };
  errors: string[];
}

// ── Date helpers ──────────────────────────────────────────────────────────────
function toIsoDate(raw: string): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  // DD/MM/YYYY
  let m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  // YYYY/MM/DD
  m = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  // YYYY-MM-DD
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(trimmed)) return trimmed;
  return trimmed;
}

function num(v: unknown): number {
  if (typeof v === 'number') return v;
  if (!v) return 0;
  const s = String(v).trim().replace(/,/g, '');
  const parsed = parseFloat(s);
  return isNaN(parsed) ? 0 : parsed;
}

// ── Sniffer ───────────────────────────────────────────────────────────────────
export function sniffCsvFormat(text: string): CsvFormat {
  const firstLine = text.split(/\r?\n/)[0] || '';

  // Format A: header row contains "Document Type","Customer Number"
  if (/^"?Document Type"?,\s*"?Customer Number"?/i.test(firstLine)) {
    return 'selected-invoices';
  }

  // Format B: first field is INV or CRD, followed by ABN-like number, ~19 columns
  const fields = firstLine.split(',');
  if (fields.length >= 19 && /^(INV|CRD)$/i.test(fields[0].trim().replace(/"/g, ''))) {
    return 'smartbiz';
  }

  return 'unknown';
}

// ── Format A: Selected-Invoices (header-based, 22 columns) ───────────────────
function parseSelectedInvoices(text: string): ParsedCsvInvoice[] {
  const result = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: h => h.trim(),
  });

  const groups = new Map<string, ParsedCsvInvoice>();

  for (const row of result.data) {
    const docNo = (row['Document Number'] || '').trim();
    if (!docNo) continue;

    const docType: 'INV' | 'CRD' = /credit/i.test(row['Document Type'] || '') ? 'CRD' : 'INV';

    const line: ParsedCsvLine = {
      supplierCode: (row['Product Code'] || '').trim(),
      description: (row['Product Description'] || '').trim(),
      qty:         num(row['Quantity Shipped/Quantity Returned']),
      unitPrice:   num(row['Unit Price']),
      lineTotal:   num(row['Line Value Excl. GST']),
      lineGst:     num(row['Line GST Amount']),
      lineIncGst:  num(row['Line Value Incl. GST']),
    };

    const existing = groups.get(docNo);
    if (existing) {
      existing.lines.push(line);
    } else {
      groups.set(docNo, {
        supplierInvoiceNo: docNo,
        docType,
        invoiceDate: toIsoDate(row['Document Date/Credit Date'] || ''),
        customerPoRef: (row['Customer Order Number'] || '').trim(),
        requesterLabel: (row['Customer Job Number/Apply to Invoice'] || '').trim(),
        subtotalExGst: num(row['Total Document Amount Excl GST']),
        totalGst:      num(row['Total Document GST']),
        totalIncGst:   num(row['Total Document Amount Incl GST']),
        lines: [line],
      });
    }
  }

  return Array.from(groups.values());
}

// ── Format B: SMARTBIZ (positional, no header, 19+ columns) ───────────────────
// Column layout (0-indexed):
// 0 docType · 1 supplierAbn · 2 acctBatch · 3 invoiceNo · 4 date ·
// 5 customerPoRef · 6 custTradingName · 7 custLegal · 8-10 address ·
// 11 supplierCode · 12 description · 13 supplierInternalId ·
// 14 qtyOrdered · 15 qtyShipped · 16 pricePer · 17 unitPrice ·
// 18 totalExGst · 19 totalGst · 20 totalIncGst
function parseSmartbiz(text: string): ParsedCsvInvoice[] {
  const result = Papa.parse<string[]>(text, {
    header: false,
    skipEmptyLines: true,
  });

  const groups = new Map<string, ParsedCsvInvoice>();

  for (const row of result.data) {
    if (!row || row.length < 18) continue;
    const [
      docTypeRaw, supplierAbn, /* acctBatch */, invoiceNo, dateRaw,
      customerPoRef, /* custTradingName */, /* custLegal */, /* addr1 */, /* addr2 */, /* addr3 */,
      supplierCode, description, /* supInternalId */,
      qtyOrdered, qtyShipped, /* pricePer */, unitPrice,
      totalExGst, totalGst, totalIncGst,
    ] = row.map(c => (c ?? '').toString().trim());

    if (!invoiceNo) continue;

    const docType: 'INV' | 'CRD' = docTypeRaw.toUpperCase() === 'CRD' ? 'CRD' : 'INV';

    const qty = num(qtyShipped) || num(qtyOrdered);
    const unit = num(unitPrice);
    const line: ParsedCsvLine = {
      supplierCode,
      description,
      qty,
      unitPrice: unit,
      lineTotal: parseFloat((qty * unit).toFixed(2)),
    };

    const existing = groups.get(invoiceNo);
    if (existing) {
      existing.lines.push(line);
    } else {
      groups.set(invoiceNo, {
        supplierInvoiceNo: invoiceNo,
        docType,
        invoiceDate: toIsoDate(dateRaw),
        customerPoRef,
        supplierAbn,
        subtotalExGst: num(totalExGst),
        totalGst: num(totalGst),
        totalIncGst: num(totalIncGst),
        lines: [line],
      });
    }
  }

  return Array.from(groups.values());
}

// ── Orchestrator ──────────────────────────────────────────────────────────────
export function parseCsvInvoiceFile(text: string): CsvDetectionResult {
  const format = sniffCsvFormat(text);
  const errors: string[] = [];
  let invoices: ParsedCsvInvoice[] = [];
  let supplierAbn: string | undefined;

  try {
    if (format === 'selected-invoices') {
      invoices = parseSelectedInvoices(text);
    } else if (format === 'smartbiz') {
      invoices = parseSmartbiz(text);
      supplierAbn = invoices.find(i => i.supplierAbn)?.supplierAbn;
    } else {
      errors.push('Unknown CSV format. Expected Selected-Invoices or SMARTBIZ format.');
    }
  } catch (err) {
    errors.push(err instanceof Error ? err.message : String(err));
  }

  // Flip signs on credit notes for reporting clarity
  for (const inv of invoices) {
    if (inv.docType === 'CRD') {
      inv.subtotalExGst = -Math.abs(inv.subtotalExGst);
      inv.totalGst = -Math.abs(inv.totalGst);
      inv.totalIncGst = -Math.abs(inv.totalIncGst);
    }
  }

  const totalLines = invoices.reduce((s, i) => s + i.lines.length, 0);
  const totalValue = invoices.reduce((s, i) => s + i.totalIncGst, 0);
  const dates = invoices.map(i => i.invoiceDate).filter(Boolean).sort();
  const dateRange = {
    from: dates[0] || '',
    to:   dates[dates.length - 1] || '',
  };

  return {
    format,
    invoices,
    supplierAbn,
    totalInvoices: invoices.length,
    totalLines,
    totalValue,
    dateRange,
    errors,
  };
}

// ── Readable label for UI ─────────────────────────────────────────────────────
export function formatLabel(format: CsvFormat): string {
  switch (format) {
    case 'selected-invoices': return 'Selected-Invoices (22-column header)';
    case 'smartbiz':          return 'SMARTBIZ (19-column positional)';
    default:                  return 'Unknown CSV format';
  }
}
